import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BadgeCheck, Clock, CreditCard, Download, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, eur, dateFr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

function FacturesAbonnement() {
  const { t } = useTranslation();
  const [factures, setFactures] = useState(null);

  useEffect(() => {
    api.get("/abonnement/factures").then((r) => setFactures(r.data)).catch(() => setFactures([]));
  }, []);

  if (!factures) return null;

  return (
    <div className="panel" data-testid="bloc-factures-abonnement">
      <h2 className="font-serif font-semibold text-lg mb-3">{t("abo.factures_titre")}</h2>
      {factures.length === 0 ? (
        <p className="text-sm text-stone-500">{t("abo.factures_vide")}</p>
      ) : (
        <div className="divide-y divide-[#E6DFD1]">
          {factures.map((f) => (
            <div key={f.numero} className="flex items-center gap-3 py-2.5" data-testid={`facture-abo-${f.numero}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm">{f.numero}</div>
                <div className="text-xs text-stone-500">
                  {dateFr(f.date)} · {f.statut === "paid" ? t("abo.payee") : f.statut}
                </div>
              </div>
              <div className="text-sm font-semibold shrink-0">{eur(f.montant)}</div>
              <a href={f.url_pdf || f.url_hote} target="_blank" rel="noreferrer"
                 className="btn-secondary !h-10 !px-3 !text-sm shrink-0"
                 data-testid={`btn-telecharger-facture-${f.numero}`}>
                <Download size={15} /> PDF
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Abonnement() {
  const { t } = useTranslation();
  const [abo, setAbo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const ATOUTS = [t("abo.atout1"), t("abo.atout2"), t("abo.atout3"), t("abo.atout4")];

  const refresh = useCallback(() => {
    api.get("/abonnement").then((r) => setAbo(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const statut = searchParams.get("statut");
    const sessionId = searchParams.get("session_id");
    if (statut === "succes" && sessionId) {
      let tries = 0;
      const poll = async () => {
        try {
          const r = await api.get(`/abonnement/statut/${sessionId}`);
          if (r.data.payment_status === "paid") {
            toast.success(t("abo.active_toast"));
            setSearchParams({});
            refresh();
            return;
          }
        } catch { /* confirmation en cours */ }
        if (++tries < 10) setTimeout(poll, 2000);
        else {
          toast.error(t("abo.confirmation_lente"));
          setSearchParams({});
          refresh();
        }
      };
      poll();
    } else if (statut === "annule") {
      toast.info(t("abo.annule_toast"));
      setSearchParams({});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function sabonner() {
    setBusy(true);
    try {
      const { data } = await api.post("/abonnement/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error(t("abo.checkout_erreur"));
      setBusy(false);
    }
  }

  async function gerer() {
    setBusy(true);
    try {
      const { data } = await api.post("/abonnement/portail", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch {
      toast.error(t("abo.portail_erreur"));
      setBusy(false);
    }
  }

  if (!abo) return <div className="py-20 text-center text-stone-500">{t("commun.chargement")}</div>;

  const dateRenouvellement = abo.periode_fin ? dateFr(abo.periode_fin) : null;

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="page-abonnement">
      <div className="text-center space-y-2">
        <div className="flex justify-center"><Logo size={56} iconSize={30} /></div>
        <h1 className="font-serif font-bold text-2xl sm:text-3xl">{t("abo.titre")}</h1>
      </div>

      {abo.statut === "bloque" && (
        <div className={`panel border-2 ${abo.essai_utilise ? "border-[#A32E20]/40" : "border-[#D9A461]/50"}`}
             data-testid="alerte-essai-termine">
          {abo.essai_utilise ? (
            <>
              <p className="font-semibold text-[#A32E20]">{t("abo.essai_termine")}</p>
              <p className="text-sm text-stone-600 mt-1">{t("abo.essai_termine_texte")}</p>
            </>
          ) : (
            <>
              <p className="font-semibold text-[#221F1A]">{t("abo.derniere_etape")}</p>
              <p className="text-sm text-stone-600 mt-1">{t("abo.derniere_etape_texte")}</p>
            </>
          )}
        </div>
      )}

      {abo.statut === "essai" && (
        <div className="panel flex items-center gap-3" data-testid="info-essai">
          <Clock size={20} className="text-[#D9A461] shrink-0" />
          <p className="text-sm text-stone-700">{t("abo.essai_reste", { count: abo.jours_restants })}</p>
        </div>
      )}

      {abo.statut === "actif" && (
        <div className="panel flex items-center gap-3 border-2 border-[#3E7C4F]/40" data-testid="info-abonnement-actif">
          <BadgeCheck size={20} className="text-[#3E7C4F] shrink-0" />
          <p className="text-sm text-stone-700">
            {abo.subscription_status === "trialing"
              ? t("abo.trialing", { date: dateRenouvellement })
              : abo.subscription_status === "past_due"
                ? t("abo.past_due")
                : `${t("abo.actif")}${dateRenouvellement ? ` — ${t("abo.renouvellement", { date: dateRenouvellement })}` : ""}.`}
          </p>
        </div>
      )}

      {abo.statut === "fondateur" && (
        <div className="panel flex items-center gap-3 border-2 border-[#D9A461]/50" data-testid="info-compte-fondateur">
          <ShieldCheck size={20} className="text-[#D9A461] shrink-0" />
          <p className="text-sm text-stone-700">{t("abo.fondateur")}</p>
        </div>
      )}

      <div className="panel space-y-5" data-testid="carte-plan">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-serif font-semibold text-lg">{abo.plan.nom}</h2>
            <p className="text-stone-500 text-sm">{t("abo.plan_texte")}</p>
          </div>
          <div className="text-right">
            <div className="font-serif font-bold text-3xl">25 € <span className="text-base font-normal text-stone-500">{t("abo.ht_mois")}</span></div>
            <div className="text-xs text-stone-500">{t("abo.tva_note")}</div>
          </div>
        </div>
        <ul className="space-y-2 text-sm text-stone-700">
          {ATOUTS.map((a) => (
            <li key={a} className="flex items-start gap-2">
              <BadgeCheck size={16} className="text-[#D9A461] mt-0.5 shrink-0" /> {a}
            </li>
          ))}
        </ul>

        {!isAdmin ? (
          <p className="text-sm text-stone-600 text-center" data-testid="info-abonnement-employe">
            {t("abo.employe_info")}
          </p>
        ) : abo.statut === "actif" || (abo.statut === "fondateur" && abo.stripe_customer) ? (
          <button onClick={gerer} disabled={busy} className="btn-secondary w-full" data-testid="btn-gerer-abonnement">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />}
            {t("abo.gerer")}
          </button>
        ) : abo.statut !== "fondateur" ? (
          <button onClick={sabonner} disabled={busy} className="btn-accent w-full !h-14 text-base" data-testid="btn-sabonner">
            {busy ? <Loader2 className="animate-spin" size={20} /> : <CreditCard size={20} />}
            {abo.essai_utilise ? t("abo.sabonner") : t("abo.commencer_essai")}
          </button>
        ) : null}

        {isAdmin && abo.statut !== "fondateur" && abo.statut !== "actif" && (
          <p className="text-xs text-stone-500 text-center">
            {t("abo.stripe_note")}
            {abo.essai_utilise ? t("abo.stripe_facture") : t("abo.stripe_essai")}
          </p>
        )}
      </div>

      {abo.stripe_customer && <FacturesAbonnement />}
    </div>
  );
}
